import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Calendar, Download, Users, Tag, CheckCircle, XCircle, MessageSquare, Activity, CreditCard, Wallet } from 'lucide-react';
import * as reportsService from '../../services/api/reports';
import { getAllUsers } from '../../services/api/admin';

interface ReportOption {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  downloadFn: (startDate: string, endDate: string) => Promise<Blob>;
  datePickerType?: 'range' | 'single';
}

const ReportsSection: React.FC = () => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date()
  });
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [users, setUsers] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getAllUsers();
        if (response.success) {
          setUsers(response.users);
        }
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };
    fetchUsers();
  }, []);

  const downloadReport = async (
    downloadFn: (startDate: string, endDate: string, userId?: string) => Promise<Blob>,
    filename: string,
    userId?: string
  ) => {
    try {
      setLoading(true);
      setError(null);
      
      const startDateStr = format(dateRange.startDate, 'yyyy-MM-dd');
      const endDateStr = format(dateRange.endDate, 'yyyy-MM-dd');
      
      const blob = await downloadFn(startDateStr, endDateStr, userId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}-${startDateStr}-to-${endDateStr}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      setError('Failed to download report. Please try again.');
      console.error('Download error:', error);
    } finally {
      setLoading(false);
    }
  };

  const reports: ReportOption[] = [
    {
      id: 'registrations',
      title: 'New Registrations',
      description: 'Report of all new user registrations',
      icon: <Users className="h-6 w-6" />,
      downloadFn: reportsService.downloadRegistrationsReport
    },
    {
      id: 'coupons-issued',
      title: 'Coupons Issued',
      description: 'Report of all coupons issued',
      icon: <Tag className="h-6 w-6" />,
      downloadFn: reportsService.downloadCouponsIssuedReport
    },
    {
      id: 'coupons-redeemed',
      title: 'Coupons Redeemed',
      description: 'Report of all redeemed coupons',
      icon: <CheckCircle className="h-6 w-6" />,
      downloadFn: reportsService.downloadCouponsRedeemedReport
    },
    {
      id: 'coupons-expired',
      title: 'Coupons Expired',
      description: 'Report of all expired coupons',
      icon: <XCircle className="h-6 w-6" />,
      downloadFn: reportsService.downloadCouponsExpiredReport
    },
    {
      id: 'support-tickets',
      title: 'Support Tickets',
      description: 'Report of all support tickets',
      icon: <MessageSquare className="h-6 w-6" />,
      downloadFn: reportsService.downloadSupportTicketsReport
    },
    {
      id: 'bot-usage',
      title: 'Bot Usage & Revenue',
      description: 'Report of all bot usage with revenue',
      icon: <Activity className="h-6 w-6" />,
      downloadFn: reportsService.downloadBotUsageReport
    },
    {
      id: 'credits',
      title: 'Credits Purchased',
      description: 'Report of all credit purchases',
      icon: <CreditCard className="h-6 w-6" />,
      downloadFn: reportsService.downloadCreditsReport
    },
    {
      id: 'credits-carried-forward',
      title: 'Credits Carried Forward',
      description: 'Report of all user credits at a specific date',
      icon: <Wallet className="h-6 w-6" />,
      downloadFn: reportsService.downloadCreditsCarriedForwardReport,
      datePickerType: 'single'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Reports</h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-gray-500" />
            <input
              type="date"
              value={format(dateRange.startDate, 'yyyy-MM-dd')}
              onChange={(e) => setDateRange(prev => ({
                ...prev,
                startDate: new Date(e.target.value),
                endDate: new Date(e.target.value) // For single date reports
              }))}
              className="text-sm border-gray-300 rounded-md"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={format(dateRange.endDate, 'yyyy-MM-dd')}
              onChange={(e) => setDateRange(prev => ({
                ...prev,
                endDate: new Date(e.target.value)
              }))}
              className="text-sm border-gray-300 rounded-md"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reports.map((report) => (
          <div
            key={report.id}
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-cyan-100 text-cyan-600 rounded-lg">
                  {report.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{report.title}</h3>
                  <p className="text-sm text-gray-500">{report.description}</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => downloadReport(report.downloadFn, report.id)}
              disabled={loading}
              className="mt-4 w-full flex items-center justify-center space-x-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              <span>{loading ? 'Downloading...' : 'Download Report'}</span>
            </button>
          </div>
        ))}

        {/* User Usage Report (Special Case) */}
        <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-cyan-100 text-cyan-600 rounded-lg">
                <Users className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">User Usage Report</h3>
                <p className="text-sm text-gray-500">Report of usage by selected user</p>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="w-full rounded-md border-gray-300 mb-4"
            >
              <option value="">Select a user</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
            <button
              onClick={() => {
                if (selectedUser) {
                  downloadReport(
                    (start, end) => reportsService.downloadUserUsageReport(selectedUser, start, end),
                    `user-usage-${selectedUser}`
                  );
                }
              }}
              disabled={loading || !selectedUser}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              <span>{loading ? 'Downloading...' : 'Download Report'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsSection;